import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enUS, it } from 'date-fns/locale';
import { Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { SendAttemptsLabel } from '../sends/SendAttemptsLabel';
import { formatSendDate } from '../sends/send-date';
import { sendsService } from '../../services/sends';
import type { Send } from '../../types/send';

interface CragSendsListProps {
  cragSlug: string;
}

/**
 * What has been climbed here, newest first.
 *
 * A crag-wide answer to "is anyone getting on this?" — the per-route drawer
 * elsewhere answers it one line at a time, which is no use when you are
 * deciding whether the place is worth the drive.
 *
 * Paged rather than whole: a cliff open for a decade has more ascents than a
 * phone should hold, and the tab is read from the top anyway.
 */
export function CragSendsList({ cragSlug }: CragSendsListProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'it' ? it : enUS;

  const [sends, setSends] = useState<Send[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);

    sendsService
      .getCragSends(cragSlug, 1)
      .then((response) => {
        if (!active) return;
        setSends(response.data);
        setHasMore(!!response.meta.has_more);
        setPage(1);
      })
      .catch(() => { if (active) setFailed(true); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [cragSlug]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const response = await sendsService.getCragSends(cragSlug, page + 1);
      setSends((current) => [...current, ...response.data]);
      setHasMore(!!response.meta.has_more);
      setPage((current) => current + 1);
    } catch {
      // The rows already read stay; the button is simply pressed again.
    } finally {
      setLoadingMore(false);
    }
  }, [cragSlug, page]);

  if (loading) {
    return (
      <div className="space-y-3 p-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (failed) {
    return <p className="p-8 text-center text-sm text-muted-foreground">{t('sends.loadError')}</p>;
  }

  if (sends.length === 0) {
    return <p className="p-8 text-center text-sm text-muted-foreground">{t('cragSends.empty')}</p>;
  }

  return (
    <div>
      <ul className="divide-y">
        {sends.map((send) => {
          const { user, climb, send_type, attempts_count, sent_at } = send.attributes;

          return (
            <li key={send.id} className="flex items-center gap-3 px-4 py-3">
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage src={user.avatar_small_url} alt={user.name || user.username} />
                <AvatarFallback>{(user.name || user.username).charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium">{climb.name}</p>
                  {/* How hard it was, so the list can be read without opening a
                      single route. A crag line without a grade yet says so
                      rather than leaving a gap the eye has to interpret. */}
                  <Badge variant="secondary" className="shrink-0 font-mono text-xs">
                    {climb.difficulty || t('climbs.noGrade')}
                  </Badge>
                </div>
                <p className="truncate text-xs text-muted-foreground">{user.name || user.username}</p>
              </div>

              {/* How it went, and when — the two things that make a row worth
                  reading once you know who and what. */}
              <div className="shrink-0 text-right text-xs">
                <SendAttemptsLabel sendType={send_type} attemptsCount={attempts_count} verbose />
                <p className="text-muted-foreground">{formatSendDate(sent_at, t, dateLocale)}</p>
              </div>
            </li>
          );
        })}
      </ul>

      {hasMore && (
        <div className="flex justify-center p-4">
          <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} className="gap-2">
            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('cragSends.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
